import React, { useRef } from "react";
import { useRootDispatch, useRootSelector } from "~redux/hooks";
import type { Container as PixiContainer } from "pixi.js";
import { setAddSaaAltId, viewPositionSelector } from "~redux/slices/edstSlice";
import { useDragging } from "hooks/useDragging";
import {
  artccIdSelector,
  colorNameMap,
  edstFontDim,
  edstFontName,
  eramFontDimensionMap,
  eramFontNameMap,
  EramInput,
  EramPrompt,
  getBitmapTextStyles,
  Line,
  MenuElement,
  saaSelector,
  selectedViewOptionSelector,
  setSelectedViewOption,
  toggleSelectedViewOption,
  useCenterCursor,
  useInputProps,
  ViewItemOptionContainer,
} from "@poscon/shared-frontend";
import { layerZIndexMap } from "~/layerZIndexMap";
import type { SaaId } from "@poscon/shared-types/eram";
import { digits, includes } from "@poscon/shared-types";
import { eramHubConnection } from "~/eramHubConnection";
import { createSaaActivationItemOptions } from "components/AirspaceStatus";

const view = "ADD_AIRSPACE_ALT_MENU";
const fontName = edstFontName;
const { width: fontWidth, height: fontHeight } = eramFontDimensionMap[fontName];
const tint = colorNameMap.lightGrey;

const keyPredicate = (c: string) => includes(digits, c);

type AddAirspaceAltitudeProps = {
  saaId: SaaId;
};

export const addAspAltWidth = fontWidth * 23;
export const addAspAltBaseHeight = fontHeight * 8 + 5;

export const AddAirspaceAltitude = ({ saaId }: AddAirspaceAltitudeProps) => {
  const dispatch = useRootDispatch();
  const ref = useRef<PixiContainer>(null);
  const artccId = useRootSelector(artccIdSelector);
  const pos = useRootSelector((state) => viewPositionSelector(state, view));
  const { startDrag } = useDragging(ref, view);
  const saa = useRootSelector((state) => saaSelector(state, saaId))!;
  const selectedViewOptionPath = useRootSelector(selectedViewOptionSelector);
  const lowerInputProps = useInputProps(
    `${view}/lowerAlt`,
    (saa.lowerAltitude / 100).toFixed(0).padStart(3, "0"),
    undefined,
    {
      maxLength: 3,
      resetOnMouseDown: true,
      keyPredicate,
    },
  );
  const upperInputProps = useInputProps(
    `${view}/upperAlt`,
    (saa.upperAltitude / 100).toFixed(0).padStart(3, "0"),
    undefined,
    {
      maxLength: 3,
      resetOnMouseDown: true,
      keyPredicate,
    },
  );

  useCenterCursor(ref);

  const rowsHeight = saa.activations.length ? saa.activations.length * (fontHeight + 3) + 1 : 0;
  const height = addAspAltBaseHeight + rowsHeight;

  return (
    <EramPrompt
      ref={ref}
      title="ADD AIRSPACE ALTITUDE"
      width={addAspAltWidth}
      height={height}
      closeMenu={() => dispatch(setAddSaaAltId(null))}
      pos={pos}
      startDrag={startDrag}
      showX
      zIndex={layerZIndexMap.prompts}
    >
      <bitmapText
        text={saa.saaName}
        x={Math.floor((addAspAltWidth + saa.saaName.length * edstFontDim.width) / 2)}
        y={fontHeight}
        style={{ ...getBitmapTextStyles(fontName), fill: tint }}
      />
      <EramInput
        {...lowerInputProps}
        x={fontWidth * 4}
        y={fontHeight * 3}
        width={fontWidth * 3 + 4}
        borderTint={colorNameMap.lightGrey}
      />
      <EramInput
        {...upperInputProps}
        x={addAspAltWidth - (fontWidth * 7 + 4)}
        y={fontHeight * 3}
        width={fontWidth * 3 + 4}
        borderTint={colorNameMap.lightGrey}
      />
      <bitmapText
        text="BOTTOM"
        x={Math.floor(fontWidth * 5.5 + 2)}
        y={fontHeight * 5}
        roundPixels
        anchor={{ x: 0.5, y: 0 }}
        style={{ ...getBitmapTextStyles(fontName), fill: tint }}
      />
      <bitmapText
        text="TOP"
        x={addAspAltWidth - Math.floor(fontWidth * 5.5 + 2)}
        y={fontHeight * 5}
        roundPixels
        anchor={{ x: 0.5, y: 0 }}
        style={{ ...getBitmapTextStyles(fontName), fill: tint }}
      />
      <MenuElement
        x={addAspAltWidth - fontWidth * 3 - 8}
        y={fontHeight * 7}
        text="ADD"
        onmousedown={() => {
          if (artccId) {
            eramHubConnection.emit("addSaaActivation", { saaId: saa.saaId, artccId: saa.artccId }, {
              artccId,
              type: "PENDING",
              floor: parseInt(lowerInputProps.value, 10) * 100,
              ceiling: parseInt(upperInputProps.value, 10) * 100,
            });
            dispatch(setAddSaaAltId(null));
          }
        }}
      />
      <Line length={addAspAltWidth - 1} y={fontHeight * 8 + 4} tint={colorNameMap.mediumGrey} />
      <container x={2} y={fontHeight * 8 + 6}>
        {saa.activations.map((activation, index) => {
          const state = activation.type === "PENDING" ? "REQ" : "ON";
          const lower = (activation.floor / 100).toFixed(0).padStart(3, "0");
          const upper = (activation.ceiling / 100).toFixed(0).padStart(3, "0");
          const text = `${state.padEnd(4, " ")} ${lower} - ${upper}`;
          const tint = activation.type === "PENDING" ? colorNameMap.yellow : colorNameMap.orange;
          const path = `${view}/addSaaAlt/${activation.id}`;
          const selected = selectedViewOptionPath === path;

          return (
            <container key={activation.id} y={index * (fontHeight + 3)}>
              <MenuElement
                text={text}
                tint={tint}
                borderColor={selected ? tint : 0}
                fillColor={0}
                selected={selected}
                selectReverseVideo
                onmousedown={() => dispatch(toggleSelectedViewOption(path))}
              />
              {selected && (
                <ViewItemOptionContainer
                  baseX={0}
                  xOffset={fontWidth * 14 + 5}
                  options={createSaaActivationItemOptions({ saaId: saa.saaId, artccId: saa.artccId }, activation, () =>
                    dispatch(setSelectedViewOption(null)),
                  )}
                />
              )}
            </container>
          );
        })}
      </container>
    </EramPrompt>
  );
};
